import React from 'react'
import { useIntl } from 'react-intl'

import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { RadioButtonType } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { ButtonSize, ButtonType } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/baseButton/BaseButton'
import {
  DropdownButton,
  DropdownComponentProps,
  DropdownMenuItem,
} from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/dropdownButton/DropdownButton'
import { RadioButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/RadioButtons'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

export type FilterOption<T extends string> = {
  label: string
  value: T
}

export const FilterMenu = styled.View(({ theme }) => ({
  gap: theme.spacing['8px'],
  padding: theme.spacing['24px'],
}))

export const getFilterDropdownItems = <T extends string>(
  options: FilterOption<T>[],
  onChange: (value: T) => void,
): DropdownMenuItem[] =>
  options.map((option) => ({
    dropdownOptionText: option.value,
    id: option.value,
    selectHandler: () => onChange(option.value),
    text: option.label,
  }))

type RadioFilterMenuProps = DropdownComponentProps & {
  options: FilterOption<string>[]
  selectedValue: string
}

export const RadioFilterMenu: React.FC<RadioFilterMenuProps> = ({ onOptionPress, options, selectedValue }) => (
  <FilterMenu>
    {options.map((option) => (
      <RadioButton
        buttonStyle={{ marginBottom: 0 }}
        buttonType={RadioButtonType.DESCRIPTION_NO_OUTLINE}
        gridButtonsGap={0}
        key={option.value}
        onChange={() => onOptionPress(option.value, { id: option.value, selectHandler: noop, text: option.label })}
        parentContainerWidth={220}
        selected={selectedValue === option.value}
        title={option.label}
        value={option.value}
        widthWrapToContent
      />
    ))}
  </FilterMenu>
)

type FilterDropdownProps = {
  customDropdownMenuComponent: React.ComponentType<DropdownComponentProps>
  customDropdownMenuComponentProps: Record<string, unknown>
  dropdownItems: DropdownMenuItem[]
  label: string
  selectedLabel: string
  testId: string
  isHighlighted?: boolean
  menuMaxHeight?: number
  outsideClickIgnoreSelectors?: string[]
  shouldCloseMenuOnOptionPress?: boolean
  zIndex: number
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  customDropdownMenuComponent,
  customDropdownMenuComponentProps,
  dropdownItems,
  label,
  selectedLabel,
  testId,
  isHighlighted = false,
  menuMaxHeight,
  outsideClickIgnoreSelectors,
  shouldCloseMenuOnOptionPress = false,
  zIndex,
}) => {
  const intl = useIntl()
  const { colors, spacing } = useTheme()

  return (
    <DropdownButton
      buttonSize={ButtonSize.EXTRA_SMALL}
      buttonType={ButtonType.TERTIARY}
      containerStyle={{ zIndex }}
      customButtonText={intl.formatMessage(
        {
          defaultMessage: '{filterLabel}: {selectedOptionLabel}',
          description: 'Filter button label and its selected value',
        },
        { filterLabel: label, selectedOptionLabel: selectedLabel },
      )}
      customDropdownMenuComponent={customDropdownMenuComponent}
      customDropdownMenuComponentProps={customDropdownMenuComponentProps}
      customTextColor={isHighlighted ? colors.textActive : colors.textSecondary}
      customTextSize={BodyTextSize.BADGE}
      dropdownItems={dropdownItems}
      dropdownStyle={{
        borderRadius: spacing['16px'],
        minWidth: '100%',
        paddingVertical: 0,
        ...(menuMaxHeight && { maxHeight: menuMaxHeight, overflow: 'scroll' }),
      }}
      dropdownWidth='max-content'
      menuMaxWidth={`calc(100vw - ${spacing['32px']})`}
      outsideClickIgnoreSelectors={outsideClickIgnoreSelectors}
      shouldCloseMenuOnOptionPress={shouldCloseMenuOnOptionPress}
      testID={tID(testId)}
      style={{
        backgroundColor: isHighlighted ? colors.backgroundHighlightPrimary : colors.backgroundElement,
        borderRadius: 100,
        height: 32,
        paddingLeft: spacing['12px'],
        paddingRight: spacing['8px'],
      }}
    />
  )
}
